import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useAppStore } from '@/stores/app-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useModuleStore } from '@/stores/module-store';
import { useUIStore } from '@/stores/ui-store';
import { HighSenderSettings } from '@/components/settings/high-sender-settings';

/**
 * First-run setup for the HighLevel conversation provider.
 *
 * HighLevel hands a provider no sending number, so until one is chosen every
 * message leaving its inbox fails — with an error the user only sees inside
 * HighLevel, not here. Rather than let them discover that by sending, the
 * choice is asked for up front.
 *
 * Deliberately not dismissable: there is nothing useful to do with the
 * HighLevel inbox until a number is set, and a "later" button would just move
 * the failure somewhere harder to explain. It closes on its own the moment a
 * number is selected.
 */
export function HighSenderSetupDialog() {
  const appCodeSAAS = useAppStore((s) => s.appCodeSAAS);
  const configured = useSettingsStore((s) => s.highSender);
  const senderLoaded = useSettingsStore((s) => s.highSenderLoaded);
  const tpaAuthenticated = useModuleStore((s) => s.tpaAuthenticated);
  const settingsOpen = useUIStore((s) => s.settingsOpen);

  // Gate on the ORG doc actually having been read, not on settingsLoaded —
  // that flag is also set on the early-return paths, so using it made the
  // dialog flash on every page load for users who already chose a number.
  if (appCodeSAAS !== 'high') return null;
  if (!senderLoaded || !tpaAuthenticated) return null;
  if (configured?.phoneNumber) return null;
  if (settingsOpen) return null;

  return (
    <Dialog open onOpenChange={() => { /* setup is required — see note above */ }}>
      <DialogContent className="max-w-lg">
        {/* The shared DialogHeader packs title and body at space-y-1.5, which is
            too tight for a three-line description — overridden here rather than
            in the shared component, which every other dialog relies on. */}
        <DialogHeader className="space-y-2.5">
          <DialogTitle>Choose your sending number</DialogTitle>
          <DialogDescription className="leading-relaxed">
            HighLevel does not provide a number to send from, so pick which of your
            Twilio numbers the HighLevel inbox should use. You can change it later in
            Settings.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-5">
          <HighSenderSettings bare />
        </div>
      </DialogContent>
    </Dialog>
  );
}
